/**
 * Admin panel — shared layout (sidebar + topbar) used by every /admin/* page.
 * Kept web-friendly: responsive, wider than the mobile phone-frame.
 */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Briefcase,
    CalendarDays,
    ShieldCheck,
    Wallet as WalletIcon,
    CreditCard,
    Flag,
    AlertOctagon,
    Settings,
    LogOut,
    Package,
    BadgeCheck,
    Activity,
    LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { closeAdminGate } from "@/lib/adminGate";

const LINKS = [
    { to: "/admin",               label: "Dashboard",      icon: LayoutDashboard, end: true },
    { to: "/admin/users",         label: "Users",          icon: Users },
    { to: "/admin/listings",      label: "Services & Events", icon: Briefcase },
    { to: "/admin/verifications", label: "Verifications",  icon: BadgeCheck },
    { to: "/admin/bookings",      label: "Bookings",       icon: Package },
    { to: "/admin/transactions",  label: "Transactions",   icon: CreditCard },
    { to: "/admin/withdrawals",   label: "Withdrawals",    icon: WalletIcon },
    { to: "/admin/disputes",      label: "Disputes",       icon: Flag },
    { to: "/admin/reports",       label: "Reports",        icon: AlertOctagon },
    { to: "/admin/support",       label: "Support",        icon: LifeBuoy },
    { to: "/admin/health",        label: "Health",         icon: Activity },
    { to: "/admin/settings",      label: "Settings",       icon: Settings },
];

const AdminLayout = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    return (
        <div className="min-h-screen w-full bg-background text-foreground flex" data-testid="admin-layout">
            <aside className="hidden md:flex w-60 shrink-0 border-r border-border bg-card flex-col">
                <div className="h-16 px-5 border-b border-border flex items-center gap-2">
                    <Logo size={28} />
                    <div>
                        <div className="font-display text-[14px] font-semibold leading-tight">Neyra</div>
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin</div>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto no-scrollbar" data-testid="admin-nav">
                    {LINKS.map((l) => {
                        const Icon = l.icon;
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) => [
                                    "flex items-center gap-2.5 h-10 px-3 rounded-xl text-[13px] font-medium transition-colors",
                                    isActive
                                        ? "bg-primary text-primary-foreground"
                                        : "text-foreground/85 hover:bg-muted",
                                ].join(" ")}
                            >
                                <Icon size={15} />
                                {l.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-border">
                    <button
                        data-testid="admin-logout"
                        onClick={() => { closeAdminGate(); if (signOut) signOut(); navigate("/admin/login"); }}
                        className="w-full flex items-center gap-2.5 h-10 px-3 rounded-xl text-[13px] font-medium text-destructive hover:bg-destructive/10"
                    >
                        <LogOut size={15} />
                        Sign out
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0">
                <div className="h-16 px-5 border-b border-border bg-card flex items-center justify-between">
                    <h1 className="font-display text-[15px] font-semibold">Administration</h1>
                    <div className="text-[12px] text-muted-foreground" data-testid="admin-whoami">
                        Signed in as <span className="text-foreground font-medium">{user?.fullName || user?.username || "Administrator"}</span>
                    </div>
                </div>
                <div className="p-5 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
